import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Collapse, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { selectActiveOrganizationId } from '@/entities/session';
import { useAppDispatch, useAppSelector } from '@/shared/model';
import { Button } from '@/shared/ui';
import type { PresetViewType } from '../../api/types';
import { getSavedPresetURL } from '../../libs/getSavedPresetURL';
import { VIEW_TYPE_CONFIG } from '../../libs/viewTypeConfig';
import { selectPresets, setupPresets } from '../../model/slice';
import type { Validator } from '../../model/types';

interface Props {
  readonly validators?: Partial<Record<PresetViewType, Validator>>;
}

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  paddingLeft: theme.spacing(1),
  cursor: 'pointer',
  userSelect: 'none',
  marginBottom: theme.spacing(1),
}));

const ItemLabel = styled('span')(() => ({
  fontSize: 14,
  fontWeight: 500,
  lineHeight: '18px',
  color: 'inherit',
  flex: '1 0 0',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export function FavoritePresetsSection({ validators }: Props) {
  const [isExpanded, setIsExpanded] = useState(true);
  const dispatch = useAppDispatch();
  const activeOrganizationId = useAppSelector(selectActiveOrganizationId);
  const presets = useAppSelector(selectPresets);

  useEffect(() => {
    dispatch(setupPresets(activeOrganizationId));
  }, [dispatch, activeOrganizationId]);
  const favorites = presets.filter((p) => {
    if (!p.isFavorite) return false;
    const validate = validators?.[p.viewType];
    return !validate || validate(p.preset).success;
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Header onClick={() => setIsExpanded((prev) => !prev)}>
        <Typography
          sx={{
            fontSize: '1rem',
            fontWeight: 500,
            lineHeight: 'normal',
            color: 'var(--blue-grey-700)',
            textTransform: 'uppercase',
            flex: 1,
          }}
        >
          Favorite Presets
        </Typography>
        <ExpandMoreIcon
          sx={{
            color: 'var(--blue-grey-700)',
            fontSize: 20,
            transition: 'transform 0.2s',
            transform: isExpanded ? 'rotate(0deg)' : 'rotate(180deg)',
          }}
        />
      </Header>
      <Collapse
        in={isExpanded}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          '& .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner': {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            mt: 0.5,
            flex: 1,
            overflowY: 'auto',
            minHeight: 0,
          }}
        >
          {favorites.length === 0 ? (
            <Typography
              sx={{
                fontSize: 12,
                color: 'var(--blue-grey-700)',
                textAlign: 'center',
                py: 1,
              }}
            >
              Mark presets as favorite to see them here
            </Typography>
          ) : (
            favorites.map((preset) => {
              const { route, Icon } = VIEW_TYPE_CONFIG[preset.viewType];

              return (
                <Button
                  key={preset.id}
                  variant="text"
                  color="secondary"
                  component={Link}
                  sx={{
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1,
                    py: 0,
                    borderRadius: 2,
                    textDecoration: 'none',
                    color: 'var(--blue-grey-1000)',
                    flexShrink: 0,

                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                  to={getSavedPresetURL({
                    baseURL: route,
                    presetId: preset.id,
                  })}
                >
                  <Icon sx={{ color: 'var(--blue-grey-700)' }} />
                  <ItemLabel>{preset.name}</ItemLabel>
                </Button>
              );
            })
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
